import React, { useId } from 'react';
import styles from './Loading.module.css';

/**
 * An SVG arc with round caps, rather than a bordered/masked div: the ends stay
 * crisp at any size and the stroke can carry a gradient.
 */
const SpinnerArc: React.FC<{ color?: string }> = ({ color }) => {
  const gradientId = `spinner-${useId()}`;

  return (
    <svg className={styles.spinnerSvg} viewBox="0 0 50 50" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color || '#6366f1'} />
          <stop offset="100%" stopColor={color || '#313485'} />
        </linearGradient>
      </defs>
      <circle className={styles.spinnerTrack} cx="25" cy="25" r="20" />
      <circle
        className={styles.spinnerArc}
        cx="25"
        cy="25"
        r="20"
        stroke={`url(#${gradientId})`}
      />
    </svg>
  );
};

export interface LoadingProps {
  /** Type of loading indicator */
  type?: 'spinner' | 'dots' | 'pulse' | 'skeleton';
  /** Size of the loading indicator */
  size?: 'small' | 'medium' | 'large';
  /** Color of the loading indicator */
  color?: string;
  /** Text to display below the loading indicator */
  text?: string;
  /** Whether to show full screen overlay */
  fullScreen?: boolean;
  /** Whether to show loading indicator inline */
  inline?: boolean;
  /** Custom className for styling */
  className?: string;
  /** Whether the loading state is active */
  loading?: boolean;
  /** Children to render when not loading */
  children?: React.ReactNode;
}

const Loading: React.FC<LoadingProps> = ({
  type = 'spinner',
  size = 'medium',
  color,
  text,
  fullScreen = false,
  inline = false,
  className = '',
  loading = true,
  children,
}) => {
  // If not loading and children provided, render children
  if (!loading && children) {
    return <>{children}</>;
  }

  // If not loading and no children, render nothing
  if (!loading) {
    return null;
  }

  const containerClasses = [
    styles.container,
    fullScreen ? styles.fullScreen : '',
    inline ? styles.inline : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const spinnerClasses = [styles.spinner, styles[type], styles[size]]
    .filter(Boolean)
    .join(' ');

  const indicator = (
    <>
      <div className={spinnerClasses} style={color ? { color } : undefined}>
        {type === 'spinner' && <SpinnerArc color={color} />}
        {type === 'dots' && (
          <>
            <div
              className={styles.dot}
              style={{ backgroundColor: color }}
            ></div>
            <div
              className={styles.dot}
              style={{ backgroundColor: color }}
            ></div>
            <div
              className={styles.dot}
              style={{ backgroundColor: color }}
            ></div>
          </>
        )}
        {type === 'pulse' && (
          <div
            className={styles.pulse}
            style={{ backgroundColor: color }}
          ></div>
        )}
      </div>
      {text && <p className={styles.text}>{text}</p>}
    </>
  );

  return (
    <div className={containerClasses}>
      {/* Full screen gets a floating card so the spinner reads as a dialog. */}
      {fullScreen ? <div className={styles.panel}>{indicator}</div> : indicator}
    </div>
  );
};

export default Loading;
